/**
 * Vertrag aus einer Vorlage anlegen (Werte aus den Stammdaten, Standardwerte und Vorbelegung der Vorlage, Unterzeichner
 * vorbelegt) und versenden (mit Platzhalter für die Nummer rendern, dann in der Datenbank prüfen, Nummer einsetzen, einfrieren).
 */
import {
  CONTRACT_NUMBER_SLOT, contractLocale, DOCUMENT_COVER_VARIABLES, documentCoverTemplate, minorStatus, prefillValues,
  type ContractRole, type CustomVariableDef, type Language,
} from "@talentkontor/domain";
import type { Api } from "../../../data/api";
import { contractContext, quellen, signingContacts } from "./kontext";
import {
  deleteDraft, loadBrandExtras, loadDealExtras, loadOrgDaten, loadOriginalDay, loadVertrag, loadVorlage, saveDraft, sendContract,
  type ContactRow, type SendResult, type SignerCapacity, type TalentFull,
} from "./daten";
import { documentOf, pdfProblem, renderCover, uploadDocument } from "./dokument";
import { renderDraft } from "./pruefen";
import { NUMBER_PREVIEW } from "./modell";
import { t } from "../../../lib/sprache";

export interface AnlegenInput {
  templateId: string;
  title: string;
  talentId: string | null;
  brandId: string | null;
  dealId: string | null;
  orgName: string;
  today: string;
  /** Nachtrag zu diesem unterschriebenen Vertrag (Praxis: ergänzt, hebt nicht auf) */
  amendsId?: string | null;
}

export function defaultValues(defs: readonly CustomVariableDef[]): Record<string, unknown> {
  const v: Record<string, unknown> = {};
  for (const d of defs) if (d.default !== undefined && d.default !== null && d.default !== "") v[d.key] = d.default;
  return v;
}

export function talentFullName(t: Pick<TalentFull, "first_name" | "last_name">): string {
  return [t.first_name, t.last_name].filter((x) => x && x.trim()).join(" ");
}

export interface ProposedSigner { role: "talent"; capacity: SignerCapacity; name: string; email: string }

/**
 * Zwei gesetzliche Vertretungen mit derselben E-Mail-Adresse (gemeinsames Postfach der Eltern, Runde 2 R2-P1): Dann unterschreibt
 * ein Elternteil. Geführt wird nur die erste Person (wie contract_send, 0119); die Oberfläche sagt es. Andere Zeilen bleiben.
 */
export function mergeSameAddressGuardians<T extends { role: string; capacity: SignerCapacity; email: string }>(rows: readonly T[]): T[] {
  const seen = new Set<string>();
  return rows.filter((r) => {
    if (r.role !== "talent" || r.capacity !== "legal_guardian") return true;
    const mail = r.email.trim().toLowerCase();
    if (!mail) return true;
    if (seen.has(mail)) return false;
    seen.add(mail);
    return true;
  });
}

/** Gesetzliche Vertretungen, die wegen derselben Adresse nicht als Unterzeichner geführt werden (für den Hinweis im Editor). */
export function sameAddressGuardians<T extends { role: string; capacity: SignerCapacity; email: string }>(rows: readonly T[]): T[] {
  const kept = new Set(mergeSameAddressGuardians(rows));
  return rows.filter((r) => !kept.has(r));
}

/**
 * Unterzeichner auf Creator-Seite (Befund 2): der Creator selbst; ist er am Tag der Unterschrift minderjährig, dazu die
 * hinterlegte gesetzliche Vertretung (beide Erziehungsberechtigte, soweit eingetragen).
 */
export function creatorSigners(t: TalentFull | null, start: string, end?: string | null, today?: string | null): ProposedSigner[] {
  if (!t) return [];
  const out: ProposedSigner[] = [{ role: "talent", capacity: "party", name: talentFullName(t), email: (t.email ?? "").toLowerCase() }];
  if (minorStatus(t.birth_date ?? null, start, end ?? null, today ?? null).minor) {
    for (const [name, email] of [[t.guardian_name, t.guardian_email], [t.guardian2_name, t.guardian2_email]] as const) {
      if ((name ?? "").trim() || (email ?? "").trim()) out.push({ role: "talent", capacity: "legal_guardian", name: (name ?? "").trim(), email: (email ?? "").trim().toLowerCase() });
    }
    // Ohne hinterlegte Vertretung bleibt eine leere Zeile, damit die Prüfung sie als offen zeigt
    if (out.length === 1) out.push({ role: "talent", capacity: "legal_guardian", name: "", email: "" });
  }
  return mergeSameAddressGuardians(out);
}

/** Unterzeichner der Marke vorschlagen; vertritt eine Agentur die Marke und hat die Marke niemanden, deren Kontakte (Praxis). */
export function brandSignerProposals(contacts: readonly ContactRow[], agencyContacts: readonly ContactRow[]): ContactRow[] {
  const own = signingContacts(contacts);
  return own.length ? own : signingContacts(agencyContacts);
}

export async function createFromTemplate(api: Api, orgId: string, input: AnlegenInput): Promise<string> {
  const tpl = await loadVorlage(api, orgId, input.templateId);
  if (!tpl) throw new Error(t("Die Vorlage wurde nicht gefunden.", "The template was not found."));
  // Nachtrag: Parteien und Kooperation aus dem Ursprungsvertrag
  const [original, originalDay] = input.amendsId ? await Promise.all([loadVertrag(api, orgId, input.amendsId), loadOriginalDay(api, input.amendsId)]) : [null, null];
  if (input.amendsId && original?.contract.status !== "signed") throw new Error(t("Ein Nachtrag ergänzt nur einen unterschriebenen Vertrag.", "An addendum only supplements a signed contract."));
  if (original) {
    input = { ...input, talentId: input.talentId ?? original.contract.talent_id, brandId: input.brandId ?? original.contract.brand_id, dealId: input.dealId ?? original.contract.deal_id };
  }
  // Aus einer Kooperation heraus stehen Creator und Marke fest
  if (input.dealId && (!input.talentId || !input.brandId)) {
    const dl = (await api.select<{ talent_id: string; brand_id: string }>("deals", { columns: "talent_id, brand_id", filters: [["id", "eq", input.dealId]] }))[0];
    if (dl) input = { ...input, talentId: input.talentId ?? dl.talent_id, brandId: input.brandId ?? dl.brand_id };
  }
  const [org, talents, brandX, dealX, terms, kinds] = await Promise.all([
    loadOrgDaten(api, orgId),
    input.talentId ? api.select<TalentFull>("talents", { filters: [["id", "eq", input.talentId]] }) : Promise.resolve([]),
    loadBrandExtras(api, input.brandId),
    loadDealExtras(api, input.dealId),
    input.dealId ? api.select<{ agency_fee_mode: string | null; agency_fee_bp: number | null; agency_fee_cents: number | null }>("deal_agency_terms", { filters: [["deal_id", "eq", input.dealId]] }).catch(() => []) : Promise.resolve([]),
    tpl.kind_id ? api.select<{ name_de: string; name_en: string }>("contract_kinds", { filters: [["id", "eq", tpl.kind_id]] }) : Promise.resolve([]),
  ]);
  const talent = talents[0] ?? null;
  const language: Language = tpl.language;
  const people = tpl.required_signers.includes("talent") ? creatorSigners(talent, input.today) : [];
  const ctx = contractContext(quellen({
    orgName: input.orgName, org, talent, brand: brandX.brand, representedBy: brandX.representedBy, contacts: brandX.contacts,
    deal: dealX.deal, items: dealX.items, periods: dealX.periods, beitraege: dealX.beitraege, plattformen: dealX.plattformen, terms: terms[0] ?? null, today: input.today,
    contract: { number: null, label: NUMBER_PREVIEW[language], title: input.title, date: input.today, kind: kinds[0] ? (language === "en" ? kinds[0].name_en : kinds[0].name_de) : null, language },
    guardians: people.filter((p) => p.capacity === "legal_guardian" && p.name).map((p) => p.name),
    original: original ? { number: original.contract.contract_number_label, title: original.contract.title, date: originalDay } : null,
  }));
  // Vorbelegung aus den Stammdaten (Provision, Auszahlungsfrist, Anschrift, Korrekturschleifen …, Befund 17), sonst der Standardwert der
  // Vorlage. Nachtest CO-009: vorher überdeckte der Standardwert (z. B. 1 Korrekturschleife) die Angabe der Kooperation.
  const values = { ...defaultValues(tpl.variables), ...prefillValues(tpl.variables, {}, ctx).values };
  const r = renderDraft({ body: tpl.body_html, annex: tpl.annex_html, defs: tpl.variables, values, context: ctx,
    locale: contractLocale(language, org.settings.locale), currency: (dealX.deal?.currency ?? org.settings.currency) as "EUR" });
  // K1 (0110): Vertrag und Unterzeichner entstehen zusammen in einer Transaktion oder gar nicht
  const id = await createDraft(api, {
    org_id: orgId, template_id: tpl.id, template_version: tpl.version, kind_id: tpl.kind_id, title: input.title.trim(), language,
    body_html: tpl.body_html, annex_html: tpl.annex_html, variable_defs: tpl.variables, variables: values,
    rendered_html: r.html, rendered_annex_html: r.annexHtml, required_signers: tpl.required_signers, annex_hidden_from: tpl.annex_hidden_from,
    talent_id: input.talentId, brand_id: input.brandId, deal_id: input.dealId, amends_id: input.amendsId ?? null,
  }, proposedSignerRows(tpl.required_signers, brandX, people));
  return id;
}

/** Unterzeichner-Zeile beim Anlegen: jede Zeile nennt alle Spalten, capacity immer ausdrücklich (K1). */
export interface DraftSignerRow {
  role: "brand" | "talent";
  slot: number;
  capacity: SignerCapacity;
  name: string;
  email: string;
  ui_language: Language | null;
  signing_position: string | null;
}

/**
 * Unterzeichner vorbelegen: Marke aus ihren Kontakten (wer „unterschreibt Verträge“ ist, sonst eine zeichnungsberechtigte
 * Person, sonst die Ansprechperson, Befund 18; hat die Marke niemanden, die Agentur der Marke), Creator und bei
 * Minderjährigen die gesetzliche Vertretung. Höchstens sechs Personen je Partei (Platz 1 bis 6).
 */
export function proposedSignerRows(required: readonly ContractRole[], brandX: Pick<Awaited<ReturnType<typeof loadBrandExtras>>, "brand" | "contacts" | "agencyContacts">,
  people: readonly ProposedSigner[]): DraftSignerRow[] {
  const rows: DraftSignerRow[] = [];
  const brand = brandX.brand;
  if (brand && required.includes("brand")) {
    const list = brandSignerProposals(brandX.contacts, brandX.agencyContacts).slice(0, 6);
    const lang: Language | null = brand.language === "en" ? "en" : brand.language === "de" ? "de" : null;
    if (list.length) {
      list.forEach((c, i) => rows.push({ role: "brand", slot: i + 1, capacity: "party", name: c.name ?? brand.name, email: (c.email ?? "").toLowerCase(),
        ui_language: lang, signing_position: c.role ?? null }));
    } else if (brand.invoice_email) {
      rows.push({ role: "brand", slot: 1, capacity: "party", name: brand.legal_name ?? brand.name, email: brand.invoice_email.toLowerCase(), ui_language: lang, signing_position: null });
    }
  }
  if (required.includes("talent")) {
    people.slice(0, 6).forEach((p, i) => {
      // Leere Vertretungszeilen legt die Datenbank nicht an (Name und Adresse fehlen); der Editor zeigt die Lücke
      if (!p.name && !p.email) return;
      rows.push({ role: "talent", slot: i + 1, capacity: p.capacity, name: p.name, email: p.email, ui_language: null, signing_position: null });
    });
  }
  return rows;
}

/** Entwurf mit Unterzeichnern in einer Transaktion anlegen (public.contract_create_draft, 0110). */
async function createDraft(api: Api, contract: Record<string, unknown>, signers: readonly DraftSignerRow[]): Promise<string> {
  const id = await api.rpc<string | null>("contract_create_draft", { p_contract: contract, p_signers: signers });
  if (!id) throw new Error(t("Der Vertrag wurde nicht angelegt.", "The contract was not created."));
  return id;
}

export interface DokumentAnlegenInput {
  file: Blob;
  fileName: string;
  title: string;
  language: Language;
  /** Parteien, die unterschreiben (Standard: Marke, Creator, Agentur) */
  required: ContractRole[];
  talentId: string | null;
  brandId: string | null;
  dealId: string | null;
  kindId: string | null;
  orgName: string;
  today: string;
  /** nur für den Demo-Bau: feste Kennung */
  id?: string;
}

/**
 * Vertrag „Dokument der Marke (PDF)“ anlegen (0071): Entwurf, PDF hochladen und mit Prüfwert anmelden, Unterschriftsseite
 * rendern, Unterzeichner vorbelegen. Scheitert das Hochladen, wird der Entwurf wieder gelöscht.
 */
export async function createFromDocument(api: Api, orgId: string, input: DokumentAnlegenInput): Promise<string> {
  const problem = await pdfProblem(input.file);
  if (problem) throw new Error(problem);
  if (!input.required.length) throw new Error(t("Bitte mindestens eine Partei wählen, die unterschreibt.", "Please choose at least one party that signs."));
  if (input.dealId && (!input.talentId || !input.brandId)) {
    const dl = (await api.select<{ talent_id: string; brand_id: string }>("deals", { columns: "talent_id, brand_id", filters: [["id", "eq", input.dealId]] }))[0];
    if (dl) input = { ...input, talentId: input.talentId ?? dl.talent_id, brandId: input.brandId ?? dl.brand_id };
  }
  const language = input.language;
  const [org, talents, brandX, dealX, kinds] = await Promise.all([
    loadOrgDaten(api, orgId),
    input.talentId ? api.select<TalentFull>("talents", { filters: [["id", "eq", input.talentId]] }) : Promise.resolve([]),
    loadBrandExtras(api, input.brandId),
    loadDealExtras(api, input.dealId),
    input.kindId ? api.select<{ name_de: string; name_en: string }>("contract_kinds", { filters: [["id", "eq", input.kindId]] }) : Promise.resolve([]),
  ]);
  const talent = talents[0] ?? null;
  const people = input.required.includes("talent") ? creatorSigners(talent, input.today) : [];
  const cover = documentCoverTemplate(language, input.required);
  // K1 (0110): Vertrag und Unterzeichner in einer Transaktion; scheitert danach das Hochladen, geht der ganze Entwurf wieder
  const row = { id: await createDraft(api, {
    ...(input.id ? { id: input.id } : {}),
    org_id: orgId, document_kind: "brand_pdf", kind_id: input.kindId, title: input.title.trim(), language, body_html: cover, annex_html: null,
    variable_defs: DOCUMENT_COVER_VARIABLES, variables: {}, rendered_html: cover, rendered_annex_html: null, required_signers: input.required,
    annex_hidden_from: [], talent_id: input.talentId, brand_id: input.brandId, deal_id: input.dealId,
  }, proposedSignerRows(input.required, brandX, people)) };
  let doc: Awaited<ReturnType<typeof uploadDocument>>;
  try {
    doc = await uploadDocument(api, orgId, row.id, input.file, input.fileName);
  } catch (e) {
    await deleteDraft(api, row.id).catch(() => undefined);
    throw e;
  }
  const ctx = contractContext(quellen({
    orgName: input.orgName, org, talent, brand: brandX.brand, representedBy: brandX.representedBy, contacts: brandX.contacts,
    deal: dealX.deal, items: dealX.items, periods: dealX.periods, terms: null, today: input.today,
    contract: { number: null, label: NUMBER_PREVIEW[language], title: input.title, date: input.today, kind: kinds[0] ? (language === "en" ? kinds[0].name_en : kinds[0].name_de) : null, language },
    guardians: people.filter((p) => p.capacity === "legal_guardian" && p.name).map((p) => p.name),
  }));
  const r = renderCover({ language, roles: input.required, doc, context: ctx, locale: contractLocale(language, org.settings.locale), currency: (dealX.deal?.currency ?? org.settings.currency) as "EUR" });
  await saveDraft(api, row.id, { variables: r.values, rendered_html: r.html });
  return row.id;
}

/**
 * Versand wie in der App: Text mit heutigem Datum und dem Platzhalter der Nummer rendern und speichern, dann in der Datenbank
 * prüfen, Nummer vergeben und einsetzen, einfrieren und die Links per Mail verschicken (Befund 26: scheitert der Versand,
 * bleibt keine Lücke im Nummernkreis).
 */
export async function versenden(api: Api, orgId: string, id: string, input: { orgName: string; today: string; message: string | null }): Promise<SendResult> {
  const [d, org] = await Promise.all([loadVertrag(api, orgId, id), loadOrgDaten(api, orgId)]);
  if (!d) throw new Error(t("Der Vertrag wurde nicht gefunden.", "The contract was not found."));
  const c = d.contract;
  const lang = c.language;
  const ctx = contractContext(quellen({
    orgName: input.orgName, org, talent: d.talent, brand: d.brand, representedBy: d.representedBy, contacts: d.contacts, deal: d.deal,
    items: d.items, periods: d.periods, beitraege: d.beitraege ?? [], plattformen: d.plattformen ?? [], terms: d.terms, today: input.today,
    contract: { number: c.contract_number, label: c.contract_number_label ?? CONTRACT_NUMBER_SLOT, title: c.title, date: input.today,
      kind: d.kind ? (lang === "en" ? d.kind.name_en : d.kind.name_de) : null, language: lang, starts_on: c.starts_on, ends_on: c.ends_on,
      term_months: c.term_months, auto_renew: c.auto_renew, renewal_months: c.renewal_months, notice_days: c.notice_days,
      agency_signatures: c.agency_signatures, exclusive_management: c.exclusive_management, exclusive_scope: c.exclusive_scope, tail_months: c.tail_months,
      agency_representatives: c.agency_representatives ?? null },
    guardians: d.signers.filter((s) => s.role === "talent" && s.capacity === "legal_guardian").map((s) => s.name),
    original: d.original ? { number: d.original.contract_number_label, title: d.original.title, date: d.original.concluded_on ?? null } : null,
  }));
  const locale = contractLocale(lang, org.settings.locale);
  const currency = (d.deal?.currency ?? org.settings.currency) as "EUR";
  // Dokument der Marke (0071): Unterschriftsseite immer aus der angemeldeten Datei und den unterschreibenden Parteien
  if (c.document_kind === "brand_pdf") {
    const cover = renderCover({ language: lang, roles: c.required_signers, doc: documentOf(c), context: ctx, locale, currency });
    await saveDraft(api, id, { body_html: cover.body, variable_defs: cover.defs, variables: cover.values, rendered_html: cover.html, rendered_annex_html: null, annex_html: null });
    return sendContract(api, id, input.message);
  }
  const r = renderDraft({ body: c.body_html, annex: c.annex_html, defs: c.variable_defs, values: c.variables, context: ctx, locale, currency });
  await saveDraft(api, id, { rendered_html: r.html, rendered_annex_html: r.annexHtml });
  return sendContract(api, id, input.message);
}
